const CAP_POW_PROTOCOL = {
  "id": 75,
  "title": "El protocolo Proof of Work (PoW)",
  "slug": "protocolo-proof-of-work-pow",
  "type": "lesson",
  "category": "blockchain",
  "level": "basic",
  "preview": "El protocolo Proof of Work (PoW) evita comportamientos maliciosos en una red exigiendo un trabajo computacional, que luego es verificado fácilmente por la red.",
  "sections": [
    {
      "heading": "¿Qué es el protocolo Proof of Work?",
      "text": [
        "El protocolo de **Prueba de Trabajo (Proof of Work, PoW)** surge para evitar comportamientos indeseados dentro de una red.",
        "Este protocolo funciona bajo el concepto de **exigir un trabajo computacional al cliente**, que luego **es verificado por la red**.",
        "Normalmente, el trabajo solicitado consiste en **realizar operaciones de cómputo complejas**, que consumen recursos antes de que se conceda acceso o se valide la acción."
      ],
      "notes": [
        "PoW protege a la red de ataques y abusos.",
        "El cliente realiza el trabajo; la red solo lo verifica.",
        "Verificar es fácil, pero producir la prueba es costoso."
      ]
    },
    {
      "heading": "¿Qué evita el PoW?",
      "text": [
        "Este mecanismo **evita que clientes maliciosos consuman los recursos de la red de manera incontrolada**.",
        "Así se impide un escenario en el que se produzca una **denegación de servicio**, afectando al resto de usuarios legítimos."
      ],
      "preview": "PoW actúa como un filtro para impedir abusos y proteger la red.",
      "notes": [
        "Previene ataques de spam.",
        "Evita el uso ilimitado de recursos.",
        "Protege contra la denegación de servicio (DoS)."
      ]
    },
    {
      "heading": "Ejemplo sencillo: Captcha",
      "text": [
        "Un ejemplo fácil de entender es el **captcha** al registrarse en una web.",
        "El sistema pone un reto que el usuario debe resolver. Si lo logra, obtiene acceso.",
        "Esto evita que atacantes creen millones de registros automáticos para colapsar la página."
      ],
      "list": [
        "Evita cuentas falsas.",
        "Protege los formularios.",
        "Filtra interacciones automatizadas."
      ]
    },
    {
      "heading": "La clave del PoW: la asimetría",
      "text": [
        "La **característica principal del PoW es la asimetría**:",
        "🔹 Para el cliente, el trabajo es **costoso y tarda tiempo**.",
        "🔹 Para la red, **verificarlo es rápido y sencillo**.",
        "Esto hace que **atacar el sistema sea caro**, pero **defenderlo sea eficiente**."
      ],
      "notes": [
        "Producir la prueba es costoso.",
        "Verificar la prueba es rápido.",
        "La red solo valida el resultado."
      ]
    },
    {
      "heading": "Satoshi Nakamoto y HashCash",
      "text": [
        "Esta característica llamó la atención de **Satoshi Nakamoto**, quien **incorporó el sistema HashCash** (un mecanismo PoW) en **Bitcoin**.",
        "Gracias a PoW, la red de Bitcoin puede **funcionar sin entidades centrales**, permitiendo que cualquiera participe con pruebas verificables."
      ]
    },
    {
      "heading": "¿Cómo funciona el protocolo PoW? (4 etapas)",
      "text": [
        "El funcionamiento del protocolo PoW se basa en **cuatro etapas fundamentales**.",
        "Cada etapa marca un paso clave en el proceso de participación, verificación y recompensa."
      ],
      "list": [
        "**Etapa 1:** El cliente o nodo establece conexión con la red, que le asigna una tarea computacionalmente costosa.",
        "**Etapa 2:** El nodo comienza a resolver el acertijo, usando mucha potencia de cómputo. A este proceso se le llama **minería**.",
        "**Etapa 3:** Cuando encuentra la solución, el nodo la envía a la red para su verificación. Si es válida, se autoriza el acceso.",
        "**Etapa 4:** Si la tarea es correcta, el cliente obtiene acceso y recibe una **recompensa por su trabajo computacional**."
      ]
    },
    {
      "heading": "¿Por qué PoW es tan útil?",
      "text": [
        "PoW logra un equilibrio excepcional entre **seguridad, participación libre y verificación sencilla**.",
        "Su diseño permite que **millones de personas participen al mismo tiempo**, manteniendo la red segura y funcional.",
        "Desde blockchains hasta sistemas de control de acceso, el PoW ofrece resultados sólidos con una estructura simple."
      ],
      "notes": [
        "Permite participación masiva.",
        "Evita alteraciones o ataques.",
        "Mantiene la integridad de la red.",
        "PoW es simple, pero extremadamente poderoso y escalable."
      ],

    }
  ]
};

export default CAP_POW_PROTOCOL;
